import React, { useEffect, useMemo, useState } from "react";
import { Tabs } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { SettlementsGeneratePage } from "./settlements-generate.page";
import { SettlementsHistoryPage } from "./settlements-history.page";
import { OpeningBalanceSeedPage } from "./opening-balance-seed.page";
import { MySettlementsPage } from "./my-settlements.page";
import {
    checkToGenerateSettlementDraft,
    viewSettlementAdmin,
    viewOwnSettlement,
    checkToSetSettlementOpeningBalance
} from "../../components/app-header/app.header.module";
import "./settlements-root.page.scss";

const { TabPane } = Tabs;

// URL hash <-> tab key sync. Keeps deep links / bookmarks working
// (e.g. /settlements#history opens the History tab).
const TAB_KEYS = {
    generate: "generate",
    history: "history",
    opening: "opening",
    mine: "mine"
};

const keyFromHash = (hash) => {
    const raw = (hash || "").replace(/^#/, "").toLowerCase();
    return TAB_KEYS[raw] || null;
};

export const SettlementsRootPage = () => {
    const history = useHistory();
    const location = useLocation();

    const tabs = useMemo(() => {
        const out = [];
        // Operator-facing tab first if user only has VIEW_OWN_SETTLEMENT
        // (so the most relevant tab is the default for partner-operator users).
        if (viewOwnSettlement()) {
            out.push({ key: TAB_KEYS.mine, label: "My Settlements", render: () => <MySettlementsPage /> });
        }
        if (checkToGenerateSettlementDraft()) {
            out.push({ key: TAB_KEYS.generate, label: "Generate", render: () => <SettlementsGeneratePage /> });
        }
        if (viewSettlementAdmin()) {
            out.push({ key: TAB_KEYS.history, label: "History", render: () => <SettlementsHistoryPage /> });
        }
        if (checkToSetSettlementOpeningBalance()) {
            out.push({ key: TAB_KEYS.opening, label: "Opening Balances", render: () => <OpeningBalanceSeedPage /> });
        }
        return out;
    }, []);

    const availableKeys = useMemo(() => tabs.map((t) => t.key), [tabs]);

    // If the URL hash points at a tab the user can't see (e.g. menu link
    // sends them to #generate but they only have VIEW_OWN_SETTLEMENT), fall
    // back to the first available tab — otherwise AntD shows an empty pane.
    const pickInitial = () => {
        const h = keyFromHash(location.hash);
        if (h && availableKeys.indexOf(h) !== -1) return h;
        return tabs[0] && tabs[0].key;
    };
    const [active, setActive] = useState(pickInitial());

    // Reflect URL hash whenever it changes (back/forward, paste a deep link),
    // but ignore hashes for tabs the user doesn't have access to.
    useEffect(() => {
        const k = keyFromHash(location.hash);
        if (k && availableKeys.indexOf(k) !== -1 && k !== active) setActive(k);
    }, [location.hash, availableKeys.join(",")]);

    // If the loaded `active` doesn't match an available tab (rare — perms changed
    // mid-session), self-heal by snapping to the first one.
    useEffect(() => {
        if (active && availableKeys.indexOf(active) === -1 && tabs[0]) {
            setActive(tabs[0].key);
        }
    }, [active, availableKeys.join(",")]);

    const onChange = (k) => {
        setActive(k);
        // Update hash without re-mounting the page
        history.replace({ pathname: location.pathname, hash: `#${k}` });
    };

    if (tabs.length === 0) return null;

    return (
        <div style={{ padding: 16 }}>
            <Tabs className="settlements-tabs" activeKey={active} onChange={onChange} type="card">
                {tabs.map((t) => (
                    <TabPane tab={t.label} key={t.key}>
                        {/* Render only the active tab's contents to avoid 3 simultaneous network calls */}
                        {active === t.key ? t.render() : null}
                    </TabPane>
                ))}
            </Tabs>
        </div>
    );
};
